import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import StatusBadge from '@/components/base/StatusBadge';
import { formatStorageBytes, formatStorageMb } from '@/lib/getStorageUsage';
import type { TenantInfo, Counts } from '../page';

interface Props {
  tenant: TenantInfo;
  counts: Counts;
  storageBytes: number;
  onTenantChange: (t: TenantInfo) => void;
  onReload: () => void;
}

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const PLAN_STYLES: Record<string, { badge: string; text: string }> = {
  free:       { badge: 'bg-slate-100 text-slate-500',    text: 'text-slate-500' },
  pro:        { badge: 'bg-amber-50 text-amber-700',     text: 'text-amber-700' },
  enterprise: { badge: 'bg-emerald-50 text-emerald-700', text: 'text-emerald-700' },
};

const STATUS_OPTS = ['active', 'trial', 'suspended', 'cancelled'];

export default function OverviewTab({ tenant, counts, storageBytes, onTenantChange, onReload }: Props) {
  const ps = PLAN_STYLES[tenant.plan_code] ?? PLAN_STYLES.free;

  const storagePct = tenant.storage_quota_mb > 0
    ? Math.min(100, Math.round((storageBytes / 1024 / 1024 / tenant.storage_quota_mb) * 100))
    : 0;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChangePlan = async () => {
    const { data: plans } = await supabase.from('saas_plans').select('id, name, price_monthly').order('price_monthly');
    const opts = (plans ?? []).reduce<Record<string, string>>((acc, p) => {
      acc[p.id] = `${p.name} — $${p.price_monthly}/mo`;
      return acc;
    }, {});
    const { value: newPlan } = await Swal.fire({
      title: 'Cambiar plan', input: 'select', inputOptions: opts,
      inputValue: tenant.plan_code, showCancelButton: true,
      confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newPlan || newPlan === tenant.plan_code) return;
    const plan = (plans ?? []).find((p) => p.id === newPlan);
    const { data: planFull } = await supabase.from('saas_plans').select('max_users, max_contracts, storage_mb').eq('id', newPlan).maybeSingle();
    const { error } = await supabase.from('saas_tenants').update({
      plan_code: newPlan,
      max_users: planFull?.max_users ?? tenant.max_users,
      max_active_contracts: planFull?.max_contracts ?? tenant.max_active_contracts,
      storage_quota_mb: planFull?.storage_mb ?? tenant.storage_quota_mb,
      updated_at: new Date().toISOString(),
    }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, plan_code: newPlan, ...(planFull ? { max_users: planFull.max_users, max_active_contracts: planFull.max_contracts, storage_quota_mb: planFull.storage_mb } : {}) });
    Swal.fire({ title: `Plan cambiado a ${plan?.name ?? newPlan}`, icon: 'success', timer: 1400, showConfirmButton: false, ...SW });
    onReload();
  };

  const handleChangeStatus = async () => {
    const opts = STATUS_OPTS.reduce<Record<string, string>>((acc, s) => { acc[s] = s.charAt(0).toUpperCase() + s.slice(1); return acc; }, {});
    const { value: newStatus } = await Swal.fire({
      title: 'Cambiar status', input: 'select', inputOptions: opts,
      inputValue: tenant.status, showCancelButton: true,
      confirmButtonText: 'Aplicar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newStatus || newStatus === tenant.status) return;
    const { error } = await supabase.from('saas_tenants')
      .update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, status: newStatus });
    Swal.fire({ title: 'Status actualizado', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
  };

  const handleSuspend = async () => {
    const isSuspended = tenant.status === 'suspended';
    const { isConfirmed } = await Swal.fire({
      title: isSuspended ? '¿Reactivar tenant?' : '¿Suspender tenant?',
      html: `<p style="color:#64748b;font-size:13px">${isSuspended ? 'El tenant volverá al estado activo.' : 'El tenant perderá acceso a la plataforma.'}</p>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: isSuspended ? 'Reactivar' : 'Suspender',
      cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: isSuspended ? '#10b981' : '#ef4444',
    });
    if (!isConfirmed) return;
    const newStatus = isSuspended ? 'active' : 'suspended';
    const { error } = await supabase.from('saas_tenants')
      .update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, status: newStatus });
    Swal.fire({ title: isSuspended ? 'Tenant reactivado' : 'Tenant suspendido', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
  };

  const handleEditName = async () => {
    const { value: newName } = await Swal.fire({
      title: 'Editar nombre', input: 'text', inputValue: tenant.name,
      showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newName || newName === tenant.name) return;
    const { error } = await supabase.from('saas_tenants').update({ name: newName, updated_at: new Date().toISOString() }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, name: newName });
    Swal.fire({ title: 'Nombre actualizado', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={handleChangePlan}
          className="flex items-center gap-2 px-3.5 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-vip-crown-line"></i></span>
          Cambiar plan
        </button>
        <button
          onClick={handleChangeStatus}
          className="flex items-center gap-2 px-3.5 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line"></i></span>
          Cambiar status
        </button>
        <button
          onClick={handleSuspend}
          className={`flex items-center gap-2 px-3.5 py-2 text-sm border rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
            tenant.status === 'suspended'
              ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'
              : 'text-red-600 border-red-200 hover:bg-red-50'
          }`}
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={tenant.status === 'suspended' ? 'ri-play-circle-line' : 'ri-pause-circle-line'}></i>
          </span>
          {tenant.status === 'suspended' ? 'Reactivar' : 'Suspender'}
        </button>
        <button
          onClick={handleEditName}
          className="flex items-center gap-2 px-3.5 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap ml-auto"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pencil-line"></i></span>
          Editar nombre
        </button>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            label: 'Status',
            content: (
              <div className="mt-2">
                <StatusBadge status={tenant.status} />
              </div>
            ),
          },
          {
            label: 'Plan',
            content: (
              <div className="mt-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${ps.badge}`}>
                  {tenant.plan_code}
                </span>
              </div>
            ),
          },
          {
            label: 'Slug',
            content: <p className="text-slate-700 text-sm font-mono mt-2">{tenant.slug}</p>,
          },
          {
            label: 'Creado',
            content: (
              <p className="text-slate-700 text-sm mt-2">
                {new Date(tenant.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            ),
          },
        ].map((item) => (
          <div key={item.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider font-medium">{item.label}</p>
            {item.content}
          </div>
        ))}
      </div>

      {/* Limits */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-slate-400 text-xs uppercase tracking-wider font-medium mb-4">Límites del plan</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { icon: 'ri-user-line', label: 'Usuarios max', value: tenant.max_users >= 999 ? 'Ilimitado' : String(tenant.max_users) },
            { icon: 'ri-file-text-line', label: 'Contratos max', value: tenant.max_active_contracts >= 9999 ? 'Ilimitado' : String(tenant.max_active_contracts) },
            { icon: 'ri-hard-drive-line', label: 'Storage quota', value: tenant.storage_quota_mb >= 1024 ? `${(tenant.storage_quota_mb / 1024).toFixed(0)} GB` : `${tenant.storage_quota_mb} MB` },
          ].map((lim) => (
            <div key={lim.label} className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 shrink-0">
                <i className={`${lim.icon} text-sm`}></i>
              </span>
              <div>
                <p className="text-slate-400 text-xs">{lim.label}</p>
                <p className="text-slate-900 text-sm font-semibold">{lim.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage counts */}
      <div>
        <p className="text-slate-400 text-xs uppercase tracking-wider font-medium mb-3">Actividad del tenant</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Clientes',  value: counts.clients,   icon: 'ri-user-3-line',    color: 'text-emerald-600', bg: 'bg-emerald-50', max: null },
            { label: 'Contratos', value: counts.contracts, icon: 'ri-file-text-line', color: 'text-amber-600',   bg: 'bg-amber-50',   max: tenant.max_active_contracts < 9999 ? tenant.max_active_contracts : null },
            { label: 'Jobs',      value: counts.jobs,      icon: 'ri-briefcase-line', color: 'text-sky-600',     bg: 'bg-sky-50',     max: null },
            { label: 'Pagos',     value: counts.payments,  icon: 'ri-bank-card-line', color: 'text-rose-600',    bg: 'bg-rose-50',    max: null },
            { label: 'Leads',     value: counts.leads,     icon: 'ri-user-add-line',  color: 'text-slate-500',   bg: 'bg-slate-100',  max: null },
          ].map((u) => (
            <div key={u.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${u.bg} ${u.color} shrink-0`}>
                <i className={`${u.icon} text-base`}></i>
              </span>
              <div>
                <p className={`text-xl font-bold leading-none ${u.color}`}>{u.value}</p>
                <p className="text-slate-400 text-xs mt-1">{u.label}</p>
                {u.max !== null && <p className="text-slate-300 text-[10px]">de {u.max}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Storage summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-slate-400">
              <i className="ri-hard-drive-line text-sm"></i>
            </span>
            <p className="text-slate-700 text-sm font-medium">Storage utilizado</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-700 font-medium">{formatStorageBytes(storageBytes)}</span>
            <span className="text-slate-300">/</span>
            <span className="text-slate-400">{formatStorageMb(tenant.storage_quota_mb)}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${storagePct > 80 ? 'bg-red-50 text-red-600' : storagePct > 50 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
              {storagePct}%
            </span>
          </div>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${storagePct > 80 ? 'bg-red-400' : storagePct > 50 ? 'bg-amber-400' : 'bg-emerald-400'}`}
            style={{ width: `${storagePct}%` }}
          />
        </div>
      </div>
    </div>
  );
}
